import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import { TranslocoService } from "@ngneat/transloco";
import { AbstractComponent } from "@components";
import {
  EventItem,
  FetchRaceByIdRequest,
  Lookups,
  Race,
  RaceDetailsComponentType,
  RaceSettings, sportsTypeVisibilityMap,
  SportType, SportTypeFieldsVisibility
} from "@model";
import { LoaderService, RaceService, TOAST_STATE, ToastService } from "@services";
import {numerical, required} from "@utility";
import { take } from "rxjs";

@Component({
  selector: 'app-race-details-basic-info',
  templateUrl: './race-details-basic-info.component.html',
  styleUrls: ['./race-details-basic-info.component.scss']
})
export class RaceDetailsBasicInfoComponent extends AbstractComponent implements OnInit {
  @Input() event?: EventItem;
  @Input() race?: Race;
  @Input() disableEditing = false;
  @Input() sports: Lookups[] = [];
  @Input() statuses: Lookups[] = [];
  @Input() registrationTypes: Lookups[] = [];
  @Input() componentType: RaceDetailsComponentType = RaceDetailsComponentType.EDIT;
  @Output() raceBasicDataChange = new EventEmitter<Race | undefined>();

  editForm = false;
  isAdmin = false;
  applicationMaxDate?: Date;
  currentDate = new Date();

  inputForm = new FormGroup({
    organizerName: new FormControl(),
    name: new FormControl(),
    nameEng: new FormControl(),
    nameIta: new FormControl(),
    nameSlo: new FormControl(),
    nameSr: new FormControl(),
    nameBs: new FormControl(),
    leagueName: new FormControl(),
    leagueNameEn: new FormControl(),
    leagueNameSl: new FormControl(),
    leagueNameIt: new FormControl(),
    leagueNameSr: new FormControl(),
    leagueNameBs: new FormControl(),
    round: new FormControl(),
    status: new FormControl(),
    registrationType: new FormControl(),
    sport: new FormControl(),
    virtualRace: new FormControl(),
    raceDates: new FormControl(),
    registrationDates: new FormControl(),
    startEvery: new FormControl(),
    maxParticipants: new FormControl(),
    length: new FormControl(),
    ascent: new FormControl(),
    descent: new FormControl(),
    maxAltitude: new FormControl(),
    minAltitude: new FormControl(),
    checkpointAmount: new FormControl(),
    refreshmentCheckpointAmount: new FormControl(),
  });
  selectedImage?: string;

  constructor(
    public translocoService: TranslocoService,
    private raceService: RaceService,
    private toastService: ToastService,
    private loaderService: LoaderService,
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToFormValueChanges();
    this.subscribeToLeagueNameChange();
    this.subscribeToCurrentRaceChange();
    this.raceItemChanged();
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['race']?.currentValue !== changes['race']?.previousValue) {
      this.raceItemChanged();
    }
  }

  private subscribeToFormValueChanges = () => {
    this.subscriptions.push(this.inputForm.controls.sport.valueChanges.subscribe(this.handleSportChange));
    this.subscriptions.push(this.inputForm.controls.raceDates.valueChanges.subscribe(() => {
      this.applicationMaxDate = this.getApplicationMaxDate();
    }));
  }

  private handleSportChange = (selectedSport: number) => {
    if (Object.values(SportType).includes(selectedSport)) {
      const sportTypeFieldsVisibility : SportTypeFieldsVisibility = sportsTypeVisibilityMap[selectedSport as SportType];
      this.handleControl("maxParticipants", sportTypeFieldsVisibility.maxParticipants, this.race?.maxParticipants);
      this.handleControl("startEvery", sportTypeFieldsVisibility.startEvery, this.race?.startEvery);
      this.handleControl("checkpointAmount", sportTypeFieldsVisibility.checkpointAmount, this.race?.checkpointAmount);
      this.handleControl("refreshmentCheckpointAmount", sportTypeFieldsVisibility.refreshmentCheckpointAmount, this.race?.refreshmentCheckpointAmount);
      this.handleControl("length", sportTypeFieldsVisibility.length, this.kmToM(this.race?.length));
      this.handleControl("ascent", sportTypeFieldsVisibility.ascent, this.race?.ascent);
      this.handleControl("descent", sportTypeFieldsVisibility.descent, this.race?.descent);
      this.handleControl("maxAltitude", sportTypeFieldsVisibility.maxAltitude, this.race?.maxAltitude);
      this.handleControl("minAltitude", sportTypeFieldsVisibility.minAltitude, this.race?.minAltitude);
    }
    this.inputForm.updateValueAndValidity();
  }

  private handleControl(formControl: string, sportTypeFieldValue: boolean | 0, value?: any) {
    const control = this.inputForm.get(formControl);
    if (!control) return;
    if (sportTypeFieldValue === 0 || sportTypeFieldValue) {
      control.enable();
      if (sportTypeFieldValue === 0) {
        control.setValue(value ?? 0);
      }
      else {
        control.setValue(value ?? null);
      }
      control.addValidators([required]);
    }
    else {
      control.removeValidators([required]);
      control.setValue(null);
      control.disable();
    }
    control.updateValueAndValidity()
    control.markAsUntouched();
  }

  private subscribeToCurrentRaceChange = () => {
    this.raceService.currentRace.subscribe((race: Race | undefined) => {
      this.race = race;
      this.raceItemChanged();
    });
  }

  private subscribeToLeagueNameChange = () => {
    const leagueNameControls = [
      this.inputForm.controls.leagueName,
      this.inputForm.controls.leagueNameEn,
      this.inputForm.controls.leagueNameSl,
      this.inputForm.controls.leagueNameIt,
      this.inputForm.controls.leagueNameSr,
      this.inputForm.controls.leagueNameBs
    ];
    leagueNameControls.forEach(control => {
      this.subscriptions.push(control.valueChanges.subscribe(this.leagueNameChanged));
    });
  }

  private leagueNameChanged = () => {
    const isLeagueNameFilled = !!(
      this.inputForm.controls.leagueName.value ||
      this.inputForm.controls.leagueNameEn.value ||
      this.inputForm.controls.leagueNameIt.value ||
      this.inputForm.controls.leagueNameSl.value ||
      this.inputForm.controls.leagueNameBs.value ||
      this.inputForm.controls.leagueNameSr.value
    );
    const roundControl = this.inputForm.controls.round;
    isLeagueNameFilled ? roundControl.setValidators([required]) : roundControl.removeValidators([required]);
    roundControl.updateValueAndValidity();
  }

  private initForm = () => {
    const requiredFields = [
      'status', 'registrationType', 'sport', 'virtualRace', 'raceDates', 'registrationDates', 'maxParticipants', 'startEvery'
    ];
    requiredFields.forEach(field => (this.inputForm.get(field) as FormControl).addValidators([required]));

    const numericFields = [
      'ascent','descent','maxAltitude','minAltitude'
    ];
    numericFields.forEach(field => (this.inputForm.get(field) as FormControl).addValidators([numerical]));
    if (this.disableEditing) this.inputForm.disable();
  }

  private populateEventDependantFields = () => {
    const event = this.event;
    const race = this.race;
    const controls = this.inputForm.controls;

    controls.organizerName.setValue(event?.organiser?.name);
    controls.organizerName.disable();

    if (event?.leagueName && !race?.leagueName) controls.leagueName.setValue(event.leagueName);
    if (event?.leagueNameEn && !race?.leagueNameEn) controls.leagueNameEn.setValue(event.leagueNameEn);
    if (event?.leagueNameIt && !race?.leagueNameIt) controls.leagueNameIt.setValue(event.leagueNameIt);
    if (event?.leagueNameSl && !race?.leagueNameSl) controls.leagueNameSl.setValue(event.leagueNameSl);
    if (event?.leagueNameSr && !race?.leagueNameSr) controls.leagueNameSr.setValue(event.leagueNameSr);
    if (event?.leagueNameBs && !race?.leagueNameBs) controls.leagueNameBs.setValue(event.leagueNameBs);
    if (event?.round && !race?.round) controls.round.setValue(event.round);
    if (event?.eventType && event.eventType !== race?.registrationType) {
      controls.registrationType.setValue(event.eventType)
    };
    controls.registrationType.disable();

    if (event?.sportOne && !race?.sport && event.sportOne !== SportType.MULTISPORT && event.sportTwo !== SportType.MULTISPORT) {
      controls.sport.setValue(event.sportOne);
    }
  }

  private raceItemChanged = () => {
    const race = this.race;
    const event = this.event;
    const controls = this.inputForm.controls;

    if (race) {
      this.inputForm.patchValue(race);
      if (this.race?.length) {
        controls.length.setValue(this.kmToM(this.race.length));
      }
      controls.raceDates.patchValue({ from: race.raceStart, to: race.raceEnd });
      controls.registrationDates.patchValue({ from: race.registrationStart, to: race.registrationEnd });
      this.selectedImage = race.imageMedium;
      this.editForm = !!race.id;
      if (!race.id) controls.virtualRace.setValue(false);
    }
    if (event) this.populateEventDependantFields();
  }

  private getRaceFields = (): Race => {
    this.checkValidators(this.inputForm);
    const raceDetails: Race = { ...this.race, ...this.inputForm.value };
    raceDetails.length = this.mToKm(this.inputForm.controls.length.value);
    raceDetails.organiser = this.event?.organiser;
    raceDetails.organiserId = this.event?.organiserId;
    raceDetails.eventId = this.event?.id;
    raceDetails.registrationStart = this.inputForm.value.registrationDates?.from;
    raceDetails.registrationEnd = this.inputForm.value.registrationDates?.to;
    raceDetails.raceStart = this.inputForm.value.raceDates?.from;
    raceDetails.raceEnd = this.inputForm.value.raceDates?.to;
    raceDetails.imageMedium = this.selectedImage;
    raceDetails.registrationType = this.inputForm.controls.registrationType.value;
    return raceDetails;
  }

  imageChange = (image?: string) => {
    this.selectedImage = image;
  }

  mToKm = (value: number | undefined) => {
    return value ? value / 1000 : undefined;
  }

  kmToM = (value: number | undefined) => {
    return value ? value * 1000 : undefined
  }

  getApplicationMaxDate = () => {
    const raceDateFrom = this.inputForm.controls.raceDates.value["from"];
    if (raceDateFrom) {
      const newDate = new Date(raceDateFrom);
      newDate.setDate(newDate.getDate());
      return newDate;
    }
    return undefined;
  }

  onSuccess = (raceId: number, key: string) => {
    this.raceService.findRaceByIdProtected(new FetchRaceByIdRequest(raceId), true)
      .pipe(take(1)).subscribe((race: Race | null) => {
      this.loaderService.dismissLoading();
      this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate(key));
      this.raceBasicDataChange.emit(race ?? undefined);
    });
  }

  onError = (key: string) => {
    this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(key));
  }

  copyRace = (raceDetails: Race) => {
    this.loaderService.startLoading();
    this.raceService.copyRace(raceDetails, (raceId: number) => {
      this.onSuccess(raceId, "app.common.success.race.copy");
    }, () => {
      this.onError("app.common.server.error");
    });
  }

  updateRace = (raceDetails: Race) => {
    this.loaderService.startLoading();
    this.raceService.updateRace(raceDetails, (raceId: number) => {
      this.onSuccess(raceId, "app.common.success.race.edit");
    }, () => {
      this.onError("app.common.server.error");
    });
  }

  onSave = () => {
    if (!this.inputForm.valid || this.disableEditing) {
      this.markAllAsTouched(this.inputForm);
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("event.item.error.form"));
      return;
    }
    if (!this.isRaceValid()) {
      return;
    }
    const raceDetails = this.getRaceFields();
    switch (this.componentType) {
      case RaceDetailsComponentType.RACE_COPY:
        this.copyRace(raceDetails);
        break;
      case RaceDetailsComponentType.ADD:
      case RaceDetailsComponentType.EDIT:
        this.updateRace(raceDetails);
        break;
      default:
        break;
    }
  }

  isRaceValid = (): boolean => {
    if (RaceSettings.isRoundRace(this.race?.raceSettings?.minimumLapDuration) && Race.isChronometerRace(this.inputForm.controls.startEvery.value)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.roundRace.startEvery"));
      return false;
    } else if (RaceSettings.isBlockRace(this.race?.raceSettings?.blockStart) && Race.isChronometerRace(this.inputForm.controls.startEvery.value)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.blockRace.startEvery"));
      return false;
    } else if (RaceSettings.isStartByGenderRace(this.race?.raceSettings?.startByType) && Race.isChronometerRace(this.inputForm.controls.startEvery.value)) {
      this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("race.details.settings.error.startByGender.startEvery"));
      return false;
    }
    return true;
  }
}

